<template>
  <div class="apoderado-form-wrapper">
    <div class="apoderado-form-container">
      <div class="form-wrapper" :class="{ 'form-completed': completed }">
      <!-- Header Section -->
      <div class="header-section">
        <h1 class="form-title">
          Datos apoderado/a financiero/a
        </h1>
        <p class="form-subtitle">
          {{ completed ? 'Datos del apoderado registrados correctamente' : 'Ingresa los datos para continuar con el pago' }}
        </p>
      </div>

      <!-- Form Fields -->
      <form 
        id="apoderadoForm"
        @submit.prevent="handleSubmit" 
        class="form-content"
        autocomplete="off"
      >
        <!-- Row 1: Nombres and Primer apellido -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="nombres">Nombres</label>
            <input
              v-model="form.nombres"
              type="text"
              id="nombres"
              name="nombres"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              onkeypress="return /[a-zA-ZáéíóúÁÉÍÓÚñÑ ]/.test(event.key)"
              maxlength="30"
              @input="validateRequiredField('nombres')"
              @blur="validateRequiredField('nombres')"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="primerApellido">Primer apellido</label>
            <input
              v-model="form.primerApellido"
              type="text"
              id="primerApellido"
              name="primerApellido"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              onkeypress="return /[a-zA-ZáéíóúÁÉÍÓÚñÑ ]/.test(event.key)"
              maxlength="12"
              @input="validateRequiredField('primerApellido')"
              @blur="validateRequiredField('primerApellido')"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 2: Segundo apellido and RUT -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="segundoApellido">Segundo apellido</label>
            <input
              v-model="form.segundoApellido"
              type="text"
              id="segundoApellido"
              name="segundoApellido"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              onkeypress="return /[a-zA-ZáéíóúÁÉÍÓÚñÑ ]/.test(event.key)"
              maxlength="12"
              @input="validateRequiredField('segundoApellido')"
              @blur="validateRequiredField('segundoApellido')"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="rutApoderado">RUT</label>
            <input
              v-model="form.rut"
              type="text"
              id="rutApoderado"
              name="rut"
              class="form-input"
              placeholder="00.000.000-K"
              :disabled="disabled"
              maxlength="12"
              @input="handleRutInput($event, 'rutApoderado')"
              @blur="handleRutBlur($event, 'rutApoderado')"
              @keypress="handleRutKeypress($event)"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 3: Celular -->
        <div class="form-row">
          <div class="form-group form-field full-width">
            <label class="field-label" for="countryCode">Celular (+56)</label>
            <div class="phone-input-container">
              <select
                v-model="form.countryCode"
                id="countryCode"
                name="countryCode"
                class="country-code-select"
                :disabled="disabled"
              >
                <option value="+56">+56</option>
              </select>
              <input
                v-model="form.telefono"
                type="tel"
                id="telefono"
                name="telefono"
                class="phone-number-input"
                placeholder="900000000"
                :disabled="disabled"
                onkeypress="return /[0-9]/i.test(event.key) || event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Tab'"
                maxlength="9"
                @input="validatePhone"
                @blur="validatePhone"
                @keyup="validatePhone"
              />
            </div>
            <div v-if="phoneError" class="error">{{ VALIDATION_MESSAGES.format.telefono }}</div>
          </div>
        </div>

        <!-- Information Box -->
        <div class="info-box">
          <div class="info-content">
            <h5>Recuerda</h5>
            <div class="item">
              <div class="bullet-yellow"></div>
              <span><strong>No utilizar emails corporativos.</strong> Puede que nuestros emails no te lleguen.</span>
            </div>
            <div class="item">
              <div class="bullet-yellow"></div>
              <span>Revisa tu casilla de <strong>Spam, Promociones o Correos no deseados</strong></span>
            </div>
            <div class="item">
              <div class="bullet-yellow"></div>
              <span>Si continúas sin recibir nuestros correos, puedes comunicarte al <strong>600 500 20 20</strong></span>
            </div>
          </div>
        </div>

        <!-- Row 4: Email and Dirección -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="emailApoderado">Email</label>
            <input
              v-model="form.email"
              type="email"
              id="emailApoderado"
              name="email"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              maxlength="50"
              @input="validateEmail"
              @blur="validateEmail"
              ref="emailInput"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="direccion">Dirección</label>
            <input
              v-model="form.direccion"
              type="text"
              id="direccion"
              name="direccion"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              maxlength="30"
              @input="validateRequiredField('direccion')"
              @blur="validateRequiredField('direccion')"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 5: Número and Departamento -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="numero">Número (opcional)</label>
            <input
              v-model="form.numero"
              type="text"
              id="numero"
              name="numero"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              onkeypress="return /[0-9]/i.test(event.key)"
              maxlength="5"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="departamento">Departamento (opcional)</label>
            <input
              v-model="form.departamento"
              type="text"
              id="departamento"
              name="departamento"
              class="form-input"
              placeholder=""
              :disabled="disabled"
              maxlength="5"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 6: Región and Ciudad -->
        <div class="form-row">
          <div class="form-group form-field">
            <label class="field-label" for="region">Región</label>
            <multiselect
              v-model="selectedRegion"
              :options="availableRegions"
              id="region"
              name="region"
              placeholder="Selecciona tu región"
              track-by="id"
              label="name"
              :showLabels="false"
              :disabled="disabled"
              class="multiselect-custom"
              @select="onSelectRegion"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.regionId"
              :name="'region'"
            />
            <div class="errorContainer"></div>
          </div>
          <div class="form-group form-field">
            <label class="field-label" for="city">Ciudad</label>
            <multiselect
              v-model="selectedCity"
              :options="availableCities"
              id="city"
              name="city"
              placeholder="Selecciona tu ciudad"
              track-by="id"
              label="name"
              :showLabels="false"
              :disabled="!form.regionId || disabled"
              class="multiselect-custom"
              @select="onSelectCity"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.cityId"
              :name="'ciudad'"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Row 7: Comuna -->
        <div class="form-row">
          <div class="form-group form-field full-width">
            <label class="field-label" for="commune">Comuna</label>
            <multiselect
              v-model="selectedCommune"
              :options="availableCommunes"
              id="commune"
              name="commune"
              placeholder="Selecciona tu comuna"
              track-by="id"
              label="name"
              :showLabels="false"
              :disabled="!form.regionId || disabled"
              class="multiselect-custom"
              @select="onSelectCommune"
            />
            <input
              autocomplete="none"
              class="withErrorContainer input-hidden"
              :value="form.communeId"
              :name="'comuna'"
            />
            <div class="errorContainer"></div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-row">
          <div class="form-field full-width">
            <!-- Contenedor de botones -->
            <div class="buttons-container">
              <!-- Botón Volver -->
              <button
                v-if="showButtonContinue"
                type="button"
                class="back-button"
                @click="goBack"
              >
                <img src="/images/iconos/IconLeft.svg" alt="Volver" class="button-icon" />
                Volver
              </button>
              
              <!-- Botón Continuar -->
              <button
                v-if="showButtonContinue"
                type="button"
                :class="buttonClass"
                :disabled="isButtonDisabled"
                @click="handleButtonClick"
              >
                  <span v-if="submitting">Verificando...</span>
                  <span v-else>
                    Continuar
                    <img src="/images/iconos/IconRight.svg" alt="Continuar" class="button-icon" />
                  </span>
                </button>
            </div>

          </div>
        </div>
      </form>
      </div>
    </div>
  </div>
</template>

<script>
import formValidation from "@/mixins/formValidation";
import rutValidation from "@/mixins/rutValidation";
import { orderBy } from "@/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { VALIDATION_MESSAGES, VALIDATION_REGEX } from "@/constants/validationMessages";
import Vue from "vue";
import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

Vue.use(VueSweetalert2);

global.jQuery = require("jquery");
const $ = global.jQuery;
window.$ = $;

export default {
  name: 'ApoderadoForm',
  components: {
    Multiselect,
  },
  mixins: [formValidation, rutValidation],
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    completed: {
      type: Boolean,
      default: false
    },
    showButtonContinue: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      VALIDATION_MESSAGES,
      VALIDATION_REGEX,
      phoneError: false,
      submitting: false,
      availableRegions: [],
      availableCities: [],
      availableCommunes: [],
      selectedRegion: null,
      selectedCity: null,
      selectedCommune: null,
      form: {
        nombres: '',
        primerApellido: '',
        segundoApellido: '',
        rut: '',
        countryCode: '+56',
        telefono: null,
        email: '',
        direccion: '',
        numero: '',
        departamento: '',
        regionId: null,
        cityId: null,
        communeId: null
      }
    }
  },
  computed: {
    isRutValid() {
      // Verificar que el RUT esté presente y sea válido
      if (!this.form.rut || this.form.rut.trim() === '') {
        return false;
      }
      
      // Verificar que no sea el placeholder o un RUT genérico
      if (this.form.rut === '00.000.000-K' || 
          this.form.rut.trim() === '00.000.000-K' ||
          this.form.rut.includes('00.000.000') ||
          this.form.rut === '00.000.000-0' ||
          this.form.rut === '00.000.000-k') {
        return false;
      }
      
      // Usar la validación del mixin
      const validation = this.validateRutChile(this.form.rut);
      
      // Verificar que el RUT tenga al menos algunos dígitos reales (no solo ceros)
      const rutNumber = this.form.rut.replace(/[^0-9]/g, '');
      const hasRealDigits = rutNumber.split('').some(digit => digit !== '0');
      
      return validation.isValid && hasRealDigits;
    },
    
    allFieldsComplete() {
      // Validar formato de email
      const emailValid = this.form.email && this.form.email.trim() && this.VALIDATION_REGEX.email.test(this.form.email);
      
      // Validar formato de teléfono
      const phoneValid = this.form.telefono && this.form.telefono.trim() && this.VALIDATION_REGEX.phone.test(this.form.telefono);
      
      const requiredFields = [
        'nombres',
        'primerApellido', 
        'segundoApellido',
        'rut',
        'direccion',
        'regionId',
        'cityId',
        'communeId'
      ];
      
      const basicFieldsValid = requiredFields.every(field => {
        const value = this.form[field];
        if (field === 'regionId' || field === 'cityId' || field === 'communeId') {
          return value && value !== null && value !== undefined && value !== '';
        } else if (field === 'rut') {
          return value && value.toString().trim() !== '' && this.isRutValid;
        } else {
          return value && value.toString().trim() !== '';
        }
      });
      return basicFieldsValid && emailValid && phoneValid;
    },

    buttonClass() {
      return 'continue-button';
    },
    
    isButtonDisabled() {
      // El botón debe estar deshabilitado si no se han completado todos los campos
      const disabled = !this.allFieldsComplete || this.submitting;
      return disabled;
    },

  },
  methods: {
    validatePhone() {
      if (!this.form.telefono || this.form.telefono.trim() === '') {
        // Campo vacío - mostrar error de requerido
        this.phoneError = true;
        this.showFieldError('telefono', VALIDATION_MESSAGES.required.telefono);
      } else if (!this.VALIDATION_REGEX.phone.test(this.form.telefono)) {
        // Formato inválido
        this.phoneError = true;
        this.showFieldError('telefono', VALIDATION_MESSAGES.format.telefono);
      } else {
        // Teléfono válido
        this.phoneError = false;
        this.clearFieldError('telefono');
      }
    },
    
    showFieldError(fieldName, message) {
      const field = document.getElementById(fieldName);
      if (field) {
        const formGroup = field.closest('.form-group');
        if (formGroup) {
          formGroup.classList.add('error');
          
          // Remover mensaje de error existente
          formGroup.querySelectorAll('label.error').forEach(label => label.remove());
          
          // Solo crear mensaje de error si no es el campo de teléfono (que ya tiene mensaje en template)
          if (fieldName !== 'telefono') {
            // Crear nuevo mensaje de error
            const errorLabel = document.createElement('label');
            errorLabel.className = 'error';
            errorLabel.textContent = message;
            errorLabel.style.color = '#ef4444';
            errorLabel.style.fontSize = '0.75rem';
            errorLabel.style.marginTop = '0.25rem';
            errorLabel.style.fontWeight = '500';
            errorLabel.style.fontStyle = 'normal';
            errorLabel.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            
            field.parentNode.insertBefore(errorLabel, field.nextSibling);
          }
        }
      }
    },
    
    clearFieldError(fieldName) {
      const field = document.getElementById(fieldName);
      if (field) {
        const formGroup = field.closest('.form-group');
        if (formGroup) {
          formGroup.classList.remove('error');
          formGroup.querySelectorAll('label.error').forEach(label => label.remove());
        }
      }
    },

    validateRequiredField(fieldName) {
      let value = this.form[fieldName];
      
      // Para campos especiales como regionId, cityId, communeId
      if (fieldName === 'regionId' || fieldName === 'cityId' || fieldName === 'communeId') {
        if (!value || value === null || value === undefined || value === '') {
          // Campo vacío - mostrar error de requerido
          this.showFieldError(fieldName, VALIDATION_MESSAGES.required[fieldName] || 'Este campo es requerido');
        } else {
          // Campo lleno - limpiar error
          this.clearFieldError(fieldName);
        }
      } else {
        // Para campos de texto normales
        if (!value || value.toString().trim() === '') {
          // Campo vacío - mostrar error de requerido
          this.showFieldError(fieldName, VALIDATION_MESSAGES.required[fieldName] || 'Este campo es requerido');
        } else {
          // Campo lleno - limpiar error
          this.clearFieldError(fieldName);
        }
      }
    },



    validateEmail() {
      // Validar formato de email
      if (!this.form.email || this.form.email.trim() === '') {
        // Campo vacío - mostrar error de requerido
        this.showFieldError('emailApoderado', VALIDATION_MESSAGES.required.email);
      } else if (this.VALIDATION_REGEX.email.test(this.form.email)) {
        // Email válido
        this.clearFieldError('emailApoderado');
      } else {
        // Email con formato inválido
        this.showFieldError('emailApoderado', VALIDATION_MESSAGES.format.email);
      }
    },

    async onSelectRegion(selectedRegion) {
      this.availableCities = [];
      this.availableCommunes = [];
      this.selectedCity = null;
      this.selectedCommune = null;
      this.form.regionId = selectedRegion ? selectedRegion.id : null;
      this.form.cityId = null;
      this.form.communeId = null;
      
      // Validar campo región
      this.validateRequiredField('regionId');
      
      const regionId = this.form.regionId;
      if (regionId) {
        const { data: cities } = await this.Axios.get(`/cities/${regionId}`);
        this.availableCities = orderBy([...cities], "name");
        const { data: communes } = await this.Axios.get(`/communes/${regionId}`);
        this.availableCommunes = orderBy([...communes], "name");
      }
    },
    onSelectCity(selectedCity) {
      this.form.cityId = selectedCity ? selectedCity.id : null;
      // Validar campo ciudad
      this.validateRequiredField('cityId');
    },
    onSelectCommune(selectedCommune) {
      this.form.communeId = selectedCommune ? selectedCommune.id : null;
      // Validar campo comuna
      this.validateRequiredField('communeId');
    },
    async handleSubmit() {
      
      // Prevenir envío múltiple
      if (this.submitting) {
        return;
      }
      
      this.submitting = true;

      try {
        
        // Primero validar que todos los campos estén llenos
        if (!this.allFieldsComplete) {
          this.submitting = false;
          return;
        }

        // Construir datos del apoderado
        const financingPartyData = {
          name: this.form.nombres,
          fatherLastName: this.form.primerApellido,
          motherLastName: this.form.segundoApellido,
          lastName: `${this.form.primerApellido} ${this.form.segundoApellido}`,
          rut: this.form.rut,
          email: this.form.email,
          phone: `${this.form.countryCode}${this.form.telefono}`,
          regionId: this.form.regionId,
          cityId: this.form.cityId,
          communeId: this.form.communeId,
          address: this.form.direccion,
          address2: this.form.numero,
          address3: this.form.departamento
        };
        
        // Emitir evento para que Step5.vue maneje la creación del apoderado
        this.$emit('apoderado-form-completed', financingPartyData);
        
      } catch (error) {
        console.error('Error en handleSubmit:', error);
      } finally {
        this.submitting = false;
      }
    },
    handleButtonClick() {
      // Llamar al método handleSubmit para procesar el formulario
      this.handleSubmit();
    },
    
    goBack() {
      // Emitir evento para volver al paso anterior
      this.$emit('go-back');
    },
  },
  async created() {
    try {
      const { data: regions } = await this.Axios.get("/regions");
      this.availableRegions = orderBy([...regions], "name");
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_form-validation.scss';
@import '@/assets/scss/_inputs-shared.scss';
@import '@/assets/scss/_rut-validation.scss';

/* Container */
.apoderado-form-container {
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.form-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 2rem;
}

.form-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
}

.form-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* Form Content */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-row .form-field.full-width {
  grid-column: 1 / -1;
}

/* Form Group - Para compatibilidad con validaciones */
.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.field-label {
  font-family: "Poppins-Medium";
  font-size: 0.875rem;
  font-weight: 100;
  color: #374151;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

/* Hidden inputs for validation */
.input-hidden {
  position: absolute;
  left: -3000px;
  visibility: hidden;
  z-index: -1000;
}

/* Information Box */
.info-box {
  background-color: #f0f7ff;
  border: 1px solid #69adf1;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 1rem 0;
}

.info-content {
  display: flex;
  flex-direction: column;
}

.info-content h5 {
  color: #115496;
  font-family: "Poppins-Medium", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  margin-bottom: 8px;
}

.item {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}

.bullet-yellow {
  margin-right: 15px;
  width: 5px;
  height: 5px;
  border-radius: 100%;
  background: #115496;
}

.item span {
  color: #115496;
  font-family: "Poppins-Medium", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

/* Continue Button */
.continue-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 160px;
  height: 48px;
  padding: 12px 24px;
  background-color: #000000;
  border: none !important;
  color: #ffffff;
  border-radius: 50px !important;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  font-family: inherit;
  flex-shrink: 0;
}

.continue-button:hover:not(:disabled) {
  background-color: #1f2937;
}

.continue-button:disabled {
  background-color: #6b7280 !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  opacity: 0.6;
  transform: none !important;
  box-shadow: none !important;
}

.continue-button:disabled:hover {
  background-color: #6b7280 !important;
  color: #9ca3af !important;
  transform: none !important;
}

.button-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* Estilo para ícono cuando el botón está deshabilitado */
.continue-button:disabled .button-icon {
  opacity: 0.6;
  filter: grayscale(100%);
}

/* Contenedor de botones */
.buttons-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
}

/* Botón Volver */
.back-button {
      border: none;
    border-radius: 24px;
    padding: 10px 26px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    background: #f5f5f5;
    color: #888;
    -webkit-transition: background 0.2s, color 0.2s;
    transition: background 0.2s, color 0.2s;
}

.back-button:hover {
  background-color: #d1d5db;
}

.back-button:active {
  transform: translateY(1px);
}

/* Responsive Design */
@media (min-width: 768px) {
  .form-row {
    grid-template-columns: 1fr 1fr;
  }
  
  .form-field.full-width {
    grid-column: 1 / -1;
  }
  
  .apoderado-form-container {
    padding: 2rem;
  }
  
  .form-wrapper {
    padding: 2.5rem;
  }
}

@media (max-width: 767px) {
  .form-wrapper {
    padding: 1.5rem;
    margin: 0 0.5rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .apoderado-form-container {
    padding: 1rem 0.5rem;
  }
}

/* Completed Form State */
.completed-badge {
  display: inline-block;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  margin-left: 1rem;
  vertical-align: middle;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>